import { Sparkles, CheckCircle2, Clock3, ArrowRight, X } from "lucide-react";

/**
 * TaskContextPanel — shows dependency context for a single task in a calm,
 * jargon-free way. Rendered inside TaskEditDialog when the API returned a
 * context object.
 *
 * Sections:
 *  - "Aktiviraće se kada..." (when this task has pending upstreams)
 *  - "Kada završiš ovo..." (when other tasks are waiting on this one)
 *  - Reopen warning (when a previously-done upstream was re-opened)
 *
 * Copy rules (product):
 *  - Never expose "dependency/parent/upstream/downstream" language.
 *  - "Uskoro" alone is banned; always paired with the concrete sentence.
 *  - Multi-upstream: "Aktiviraće se kada se završe još N stvari." + itemized list.
 *  - Sleeping is a calm state (saved for later), not an error.
 */
export default function TaskContextPanel({ context, onDismissReopenWarning }) {
  if (!context) return null;
  const {
    is_sleeping,
    activated_at,
    waiting_for = [],
    ready_upstreams = [],
    pending_upstreams = [],
    unlocks = [],
    unlock_note,
    reopen_warning,
  } = context;

  const showWaiting = waiting_for.length > 0;
  const showUnlocks = unlocks.length > 0;
  const showReopen = !!reopen_warning;
  if (!showWaiting && !showUnlocks && !showReopen) return null;

  return (
    <div className="space-y-3" data-testid="task-context-panel">
      {/* ---- Reopen warning (top) ---- */}
      {showReopen && (
        <div
          data-testid="task-reopen-warning"
          className="rounded-md border border-ochre/40 bg-ochre/10 p-3 flex items-start gap-2.5"
        >
          <Clock3 className="w-4 h-4 mt-0.5 text-ochre shrink-0" aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-ink leading-snug">{reopen_warning}</div>
          </div>
          {onDismissReopenWarning && (
            <button
              type="button"
              onClick={onDismissReopenWarning}
              data-testid="task-reopen-dismiss"
              className="text-xs text-ink-muted hover:text-forest shrink-0 px-2 py-1"
            >
              razumem
            </button>
          )}
        </div>
      )}

      {/* ---- Aktiviraće se kada... — reduced visual weight (informativni blok, ne dominantan) ---- */}
      {showWaiting && (
        <section
          data-testid="task-activation-section"
          className="rounded-md border border-line bg-surface-secondary/60 px-3 py-2.5 text-xs"
        >
          <div className="flex items-center gap-2 text-forest text-[11px] font-medium uppercase tracking-wide">
            <Sparkles className="w-3 h-3" aria-hidden />
            {is_sleeping
              ? "Aktiviraće se kada..."
              : activated_at
              ? "Otvorilo se nakon"
              : "Uslov"}
          </div>

          <p className="mt-1.5 text-sm text-ink leading-snug" data-testid="task-activation-sentence">
            {activationSummary(pending_upstreams, waiting_for)}
          </p>

          {unlock_note && (
            <p className="mt-1 text-[11px] text-ink-muted italic" data-testid="task-unlock-note">
              „{unlock_note}"
            </p>
          )}

          {waiting_for.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="task-upstream-list">
              {waiting_for.map((u) => (
                <UpstreamItem key={u.id} item={u} />
              ))}
            </ul>
          )}

          {waiting_for.length > 1 && (
            <div className="mt-1.5 text-[10px] text-ink-muted" data-testid="task-upstream-summary">
              Završeno {ready_upstreams.length} od {waiting_for.length}.
            </div>
          )}
        </section>
      )}

      {/* ---- Kada završiš ovo... ---- */}
      {showUnlocks && (
        <section
          data-testid="task-unlocks-section"
          className="rounded-md border border-forest/25 bg-forest/[0.04] p-3"
        >
          <div className="flex items-center gap-2 text-forest text-sm font-medium">
            <ArrowRight className="w-4 h-4" aria-hidden />
            Kada završiš ovo, otvoriće se
          </div>
          <ul className="mt-2 space-y-1.5" data-testid="task-unlocks-list">
            {unlocks.map((d) => (
              <li
                key={d.id}
                data-testid={`task-unlock-${d.id}`}
                className="text-sm text-ink flex items-start gap-2"
              >
                <span className="text-forest mt-1" aria-hidden>·</span>
                <span className="flex-1 min-w-0 break-words">{d.title}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-ink-muted">
            {unlocks.length === 1
              ? "Ovaj task otvara još jedan naredni korak."
              : `Ovaj task otvara još ${unlocks.length} naredna koraka.`}
          </p>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------
function activationSummary(pending, all) {
  // All satisfied → task is active or freshly activated
  if (!pending || pending.length === 0) {
    if (all && all.length > 0) return "Svi uslovi su ispunjeni.";
    return "Uslov je ispunjen.";
  }
  if (pending.length === 1) {
    const first = firstPending(all);
    if (first) {
      const person = first.collaborator_name?.split(" ")[0];
      const title = (first.title || "").trim().replace(/\.$/, "");
      return person
        ? `Kada ${person} završi „${title}".`
        : `Kada se završi „${title}".`;
    }
    return "Kada se završi prethodni korak.";
  }
  return `Kada se završe još ${pending.length} stvari.`;
}

function firstPending(items) {
  return (items || []).find((u) => !u.is_done);
}

function UpstreamItem({ item }) {
  const done = item.is_done;
  return (
    <li
      data-testid={`task-upstream-${item.id}`}
      className="text-sm flex items-start gap-2"
    >
      {done ? (
        <CheckCircle2 className="w-4 h-4 mt-0.5 text-forest shrink-0" aria-hidden />
      ) : (
        <span
          className="w-4 h-4 mt-0.5 shrink-0 rounded-full border-2 border-ink-muted/40 inline-block"
          aria-hidden
        />
      )}
      <div className="flex-1 min-w-0">
        <div className={`leading-snug ${done ? "text-ink-muted line-through" : "text-ink"}`}>
          {item.title}
          {item.collaborator_name && (
            <span className="text-ink-muted"> · {item.collaborator_name}</span>
          )}
        </div>
        <div className="text-[11px] text-ink-muted mt-0.5">
          {done ? "Završeno" : "Čeka"}
        </div>
      </div>
    </li>
  );
}
